// @ts-check
// ════════════════════════════════════════════════════════════════
// FloorCost — persisted-value read boundary (TYPE-2)
// ────────────────────────────────────────────────────────────────
// The read half of the localStorage contract. `lsSet` has guarded the
// write side since P2; the read side was hand-rolled at every call
// site as:
//
//   try { return JSON.parse(localStorage.getItem(K)) || []; }
//   catch { return []; }
//
// That guard catches a bad *parse* but not a bad *shape*. `{"a":1}`,
// `"text"` and `42` are all truthy, so they survive the `|| []` and
// reach the caller as if they were the array it asked for — and the
// first `.filter()`/`.unshift()` downstream throws a TypeError far
// from the corrupt key that caused it.
//
// Two modules already hand-rolled the missing check correctly
// (`tracker/views.js` filters after an `Array.isArray` test,
// `tracker/sort.js` type-tests each field). This module is that same
// check, written once, with the outcome reported rather than
// swallowed so a corrupt key is diagnosable.
//
// Pure by design — takes the raw string, returns plain data, touches
// neither `localStorage` nor the DOM. `#features/sync/storage.js`
// owns the actual read; this owns what the bytes are allowed to mean.
// ════════════════════════════════════════════════════════════════

/**
 * What a stored value turned out to be.
 *
 * - `ok` — parsed and matched the expected shape.
 * - `empty` — nothing stored (absent key, empty string, or literal
 *   `null`). Not a fault: it is the normal first-run state, and the
 *   caller gets the same empty default as before.
 * - `unparseable` — present but not valid JSON. Corruption.
 * - `wrong-shape` — valid JSON of the wrong type. Corruption, and the
 *   case the old `|| []` guard let through silently.
 *
 * @typedef {'ok'|'empty'|'unparseable'|'wrong-shape'} StoredReadStatus
 */

/**
 * @typedef {Object} StoredArrayRead
 * @property {unknown[]} value   Always an array — the parsed one when `status`
 *                               is `ok`, otherwise a fresh empty array, so a
 *                               caller that ignores `status` still cannot crash.
 * @property {StoredReadStatus} status
 */

/**
 * Parse a raw localStorage string that is expected to hold a JSON array.
 *
 * Never throws and never returns a non-array, which is the whole point:
 * the caller's `Array` assumption is guaranteed by this function rather
 * than assumed at each of its own use sites.
 *
 * An empty string is treated as `empty` rather than `unparseable`.
 * `JSON.parse('')` does throw, but a zero-length value carries no
 * evidence of corruption — it is indistinguishable from a key that was
 * written blank — and reporting it as a fault would put noise in the
 * console for a benign state.
 *
 * @param {string|null|undefined} raw  The value as `localStorage.getItem` returned it
 * @returns {StoredArrayRead}
 */
export function parseStoredArray(raw) {
  if (raw == null || raw === '') return { value: [], status: 'empty' };

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { value: [], status: 'unparseable' };
  }

  // Literal `null` is a legitimately stored "nothing", not corruption —
  // the previous `|| []` guard treated it the same way.
  if (parsed == null) return { value: [], status: 'empty' };

  if (!Array.isArray(parsed)) return { value: [], status: 'wrong-shape' };

  return { value: parsed, status: 'ok' };
}

/**
 * @typedef {Object} StoredObjectRead
 * @property {Record<string, unknown>|null} value  The parsed object when `status`
 *                               is `ok`, otherwise `null` — the shape every
 *                               caller here already treats as "nothing saved",
 *                               so one that ignores `status` behaves as before.
 * @property {StoredReadStatus} status
 */

/**
 * Parse a raw localStorage string that is expected to hold a JSON object.
 *
 * The object counterpart of `parseStoredArray`, with the same four statuses
 * and the same guarantee: never throws, and never returns something the
 * caller's `typeof x === 'object'` assumption would be wrong about.
 *
 * Arrays are `wrong-shape`, not `ok`. `typeof [] === 'object'` in JavaScript,
 * so a caller reaching for named fields on a stored array would read
 * `undefined` from every one of them and carry on as though the value were
 * merely empty — the same silent-wrong-shape failure the array parser exists
 * to stop, pointing the other way.
 *
 * `null` returns `empty` rather than `ok` for the same reason it does there:
 * a legitimately stored "nothing" is not corruption.
 *
 * An empty string does NOT, and this is where the two parsers deliberately
 * diverge. `getItem` answers `null` for an absent key and `''` only for a key
 * that exists holding zero bytes — so unlike `null`, `''` is distinguishable
 * from first run. It also cannot be something this app wrote: every write goes
 * through `lsSet(key, JSON.stringify(x))`, and the shortest object that
 * produces is `{}`, two characters. A zero-length value is therefore evidence
 * that a snapshot was truncated, not a benign state, and the object callers
 * are exactly the ones that warn the operator — `loadFormState` in red, naming
 * Import as the recovery. Reporting `empty` there would swallow that warning,
 * which is the failure this whole slice exists to prevent.
 *
 * `parseStoredArray` keeps its own `''`-is-`empty` behaviour: that call was
 * made deliberately in #890, its callers only log rather than telling anyone,
 * and changing it would move eight existing sites. The same argument arguably
 * applies to it and is worth revisiting on its own evidence, not silently here.
 *
 * @param {string|null|undefined} raw  The value as `localStorage.getItem` returned it
 * @returns {StoredObjectRead}
 */
export function parseStoredObject(raw) {
  if (raw == null) return { value: null, status: 'empty' };
  if (raw === '') return { value: null, status: 'unparseable' };

  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { value: null, status: 'unparseable' };
  }

  if (parsed == null) return { value: null, status: 'empty' };

  if (typeof parsed !== 'object' || Array.isArray(parsed)) {
    return { value: null, status: 'wrong-shape' };
  }

  return { value: /** @type {Record<string, unknown>} */ (parsed), status: 'ok' };
}
